export type QrScanStop = () => void

type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>
}

function hasLiveBarcodeDetector(): boolean {
  return Boolean(
    (window as Window & { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector,
  )
}

/** Safari iOS / PWA: scanner nativo spesso assente — usa il pulsante fotocamera. */
export function isLiveQrScannerLikelySupported(): boolean {
  return hasLiveBarcodeDetector() && Boolean(navigator.mediaDevices?.getUserMedia)
}

async function startJsQrVideoScanner(
  video: HTMLVideoElement,
  onCode: (raw: string) => void,
): Promise<QrScanStop | null> {
  const { default: jsQR } = await import('jsqr')
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' } },
    audio: false,
  })
  video.srcObject = stream
  video.setAttribute('playsinline', 'true')
  video.muted = true
  await video.play()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    stream.getTracks().forEach((t) => t.stop())
    video.srcObject = null
    return null
  }

  let running = true
  let lastRaw = ''

  const loop = (): void => {
    if (!running) return
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      if (canvas.width > 0 && canvas.height > 0) {
        ctx.drawImage(video, 0, 0)
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const found = jsQR(img.data, img.width, img.height)
        const raw = found?.data?.trim() ?? ''
        if (raw && raw !== lastRaw) {
          lastRaw = raw
          onCode(raw)
          return
        }
      }
    }
    requestAnimationFrame(loop)
  }
  requestAnimationFrame(loop)

  return () => {
    running = false
    stream.getTracks().forEach((t) => t.stop())
    video.srcObject = null
  }
}

async function startBarcodeDetectorScanner(
  video: HTMLVideoElement,
  onCode: (raw: string) => void,
): Promise<QrScanStop | null> {
  const BarcodeDetectorApi = (window as Window & { BarcodeDetector?: BarcodeDetectorCtor })
    .BarcodeDetector!
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' } },
    audio: false,
  })
  video.srcObject = stream
  video.setAttribute('playsinline', 'true')
  video.muted = true
  await video.play()

  const detector = new BarcodeDetectorApi({ formats: ['qr_code'] })
  let running = true
  let lastRaw = ''

  const loop = async (): Promise<void> => {
    if (!running) return
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      try {
        const codes = await detector.detect(video)
        const raw = codes[0]?.rawValue?.trim() ?? ''
        if (raw && raw !== lastRaw) {
          lastRaw = raw
          onCode(raw)
          return
        }
      } catch {
        /* skip frame */
      }
    }
    requestAnimationFrame(() => void loop())
  }
  void loop()

  return () => {
    running = false
    stream.getTracks().forEach((t) => t.stop())
    video.srcObject = null
  }
}

/** Scanner live QR (BarcodeDetector, altrimenti jsQR + fotocamera). */
export async function startLiveQrScanner(
  video: HTMLVideoElement,
  onCode: (raw: string) => void,
  onError?: (msg: string) => void,
): Promise<QrScanStop | null> {
  if (!navigator.mediaDevices?.getUserMedia) {
    onError?.('Fotocamera non disponibile su questo browser.')
    return null
  }

  try {
    if (hasLiveBarcodeDetector()) {
      return await startBarcodeDetectorScanner(video, onCode)
    }
    return await startJsQrVideoScanner(video, onCode)
  } catch (err) {
    onError?.(err instanceof Error ? err.message : String(err))
    return null
  }
}
